import React, { useMemo, useState, useEffect } from "react";
// outsource
import { Layout, Col, Row, Modal, message } from "antd";
// redux
import { useDispatch, useSelector } from "react-redux";
import {
  snapPayoutDaily,
  unSnapPayoutDaily,
  calculatePayoutDailyData,
} from "../../store/payout_daily/payout_daily.action";
import {
  payoutDailySelector,
  payoutDailyLoadingSelector,
} from "../../store/payout_daily/payout_daily.selector";
import {
  snapPayoutStatus,
  unSnapPayoutStatus,
  markPayoutComplete,
  markPayoutUncomplete,
} from "../../store/payout_status/payout_status.action";
import { payoutStatusMapSelector } from "../../store/payout_status/payout_status.selector";
import {
  snapGeneral,
  unSnapGeneral,
} from "../../store/payout_setting/payout_setting.action";
import { generalSelector } from "../../store/payout_setting/payout_setting.selector";
import { snapBranchs, unSnapBranchs } from "../../store/branch/branch.action";
import { branchsSelector } from "../../store/branch/branch.selector";
import { userSelector } from "../../store/user/user.selector";
// components
import PayoutToolbar from "./component/payout_toolbar";
import PayoutTable from "./component/payout_table";

import LayoutBreadCrumb from "../00_Nav/layout/layout_bread_crumb";
import LayoutFooter from "../00_Nav/layout/layout_footer";
// dialogs
import PayoutCardDialog from "./dialog/payout_card_dialog";
import PayoutDownloadOptionDialog from "./dialog/payout_download_option_dialog";
// functions
import {
  formatMoney,
  formatHours,
  formatDate,
} from "./functions/payout_list_format";
import {
  getStartOfWeek,
  addDays,
  toDateNum,
} from "./functions/payout_list_date";
import {
  downloadPayoutList,
  downloadPayoutCard,
} from "./functions/payout_list_download";
import { aggregateWeeklyPayout } from "./functions/payout_list_calculation";
// style
import "./style.css";
import { URL } from "../00_Nav/url";

const { Content } = Layout;

const PayoutScreen = () => {
  // -------------------------- VAR --------------------------
  const breadCrumb = [
    { title: "Home", url: URL.home },
    { title: "Payout", url: URL.payout },
    { title: "Payout List", url: URL.payoutList },
  ];

  // -------------------------- REDUX --------------------------
  const dispatch = useDispatch();
  const dailyRecords = useSelector(payoutDailySelector);
  const loading = useSelector(payoutDailyLoadingSelector);
  const payoutStatusMap = useSelector(payoutStatusMapSelector);
  const payoutGeneral = useSelector(generalSelector);
  const branchs = useSelector(branchsSelector);
  const currentUser = useSelector(userSelector);

  // -------------------------- STATE --------------------------
  const [currentWeekStart, setCurrentWeekStart] = useState(
    getStartOfWeek(new Date()),
  );
  const [keyword, setKeyword] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [viewingStaff, setViewingStaff] = useState(null);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // -------------------------- EFFECT --------------------------
  useEffect(() => {
    const start = toDateNum(currentWeekStart);
    const end = toDateNum(addDays(currentWeekStart, 6));
    dispatch(snapPayoutDaily({ startDate: start, endDate: end }));
    dispatch(snapPayoutStatus({ periodStart: start }));
    setSelectedRowKeys([]);
    return () => {
      dispatch(unSnapPayoutDaily());
      dispatch(unSnapPayoutStatus());
    };
  }, [dispatch, currentWeekStart]);

  useEffect(() => {
    dispatch(snapGeneral());
    dispatch(snapBranchs());
    return () => {
      dispatch(unSnapGeneral());
      dispatch(unSnapBranchs());
    };
  }, [dispatch]);

  // -------------------------- FUNCTION --------------------------
  const weekEnd = useMemo(
    () => addDays(currentWeekStart, 6),
    [currentWeekStart],
  );

  const payPeriodText = useMemo(
    () => `${formatDate(currentWeekStart)} - ${formatDate(weekEnd)}`,
    [currentWeekStart, weekEnd],
  );
  const afterHourRate = payoutGeneral?.afterHourRate ?? 1;
  const holidayRate = payoutGeneral?.holidayRate ?? 1;

  // branch id → shop name shown in the payout card shop breakdown
  const branchNames = useMemo(
    () =>
      Object.fromEntries(
        (branchs || []).map((branch) => [
          branch.id,
          branch.shopId || branch.name || "",
        ]),
      ),
    [branchs],
  );

  // aggregate dailyRecords → one row per staff for the week
  const data = useMemo(
    () => aggregateWeeklyPayout(dailyRecords, payoutStatusMap, branchNames),
    [dailyRecords, payoutStatusMap, branchNames],
  );

  const filteredData = useMemo(() => {
    const q = String(keyword || "")
      .trim()
      .toLowerCase();
    if (!q) return data;
    return data.filter((item) =>
      String(item.fullName || "")
        .toLowerCase()
        .includes(q),
    );
  }, [data, keyword]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const selectedPayoutData = useMemo(
    () => filteredData.filter((item) => selectedRowKeys.includes(item.id)),
    [filteredData, selectedRowKeys],
  );

  const totalCash = useMemo(
    () => filteredData.reduce((sum, item) => sum + Number(item.cash || 0), 0),
    [filteredData],
  );

  const totalBank = useMemo(
    () => filteredData.reduce((sum, item) => sum + Number(item.bank || 0), 0),
    [filteredData],
  );

  const totalPayout = useMemo(
    () =>
      filteredData.reduce(
        (sum, item) => sum + Number(item.totalPayout || 0),
        0,
      ),
    [filteredData],
  );

  const nextDisabled = useMemo(() => {
    const currentStart = getStartOfWeek(new Date());
    return currentWeekStart.getTime() >= currentStart.getTime();
  }, [currentWeekStart]);

  const handlePreviousWeek = () =>
    setCurrentWeekStart((prev) => addDays(prev, -7));

  const handleNextWeek = () => {
    if (nextDisabled) return;
    setCurrentWeekStart((prev) => {
      const next = addDays(prev, 7);
      const currentStart = getStartOfWeek(new Date());
      return next.getTime() > currentStart.getTime() ? currentStart : next;
    });
  };

  const handleWeekChange = (date) => {
    if (!date) return;
    setCurrentWeekStart(getStartOfWeek(date.toDate()));
    setPage(1);
  };

  const handleSelectOne = (id, checked) => {
    setSelectedRowKeys((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id),
    );
  };

  const handleSelectAllPage = (checked) => {
    const pageIds = pagedData.map((x) => x.id);
    setSelectedRowKeys((prev) =>
      checked
        ? [...new Set([...prev, ...pageIds])]
        : prev.filter((id) => !pageIds.includes(id)),
    );
  };

  const handleCompletePayout = () => {
    if (!selectedRowKeys.length) return;
    Modal.confirm({
      title: "Complete payout",
      content: `Do you want to mark ${selectedRowKeys.length} staff payout(s) as completed?`,
      okText: "Confirm",
      cancelText: "Cancel",
      centered: true,
      className: "complete-payout-confirm-modal",
      okButtonProps: {
        style: { background: "#385898", borderColor: "#385898" },
      },
      onOk: () => {
        const periodStart = toDateNum(currentWeekStart);
        const periodEnd = toDateNum(weekEnd);
        selectedRowKeys.forEach((staffId) => {
          const item = data.find((d) => d.id === staffId);
          dispatch(
            markPayoutComplete({
              data: {
                staff: staffId,
                periodStart,
                periodEnd,
                bank: item?.bank || 0,
                cash: item?.cash || 0,
                totalPayout: item?.totalPayout || 0,
                totalHours: item?.totalHrs || 0,
                gross: item?.gross || 0,
                category: item?.category || "",
                payrate: item?.payrate || 0,
                nonXeroPayrate: item?.nonXeroPayrate || 0,
                xeroPayrate: item?.xeroPayrate || 0,
                xeroHours: item?.xeroHours || 0,
                xeroPay: item?.xeroPay || 0,
                includeXeroHours: item?.includeXeroHours ?? false,
                kiwiSaver: item?.kiwiSaver || 0,
                fuelSupport: item?.fuelSupport || 0,
                actionBy: currentUser?.id || null,
              },
            }),
          );
        });
        setSelectedRowKeys([]);
      },
    });
  };

  const handleToggleStatus = (item, checked) => {
    const periodStart = toDateNum(currentWeekStart);
    const periodEnd = toDateNum(weekEnd);
    if (checked) {
      dispatch(
        markPayoutComplete({
          data: {
            staff: item.id,
            periodStart,
            periodEnd,
            bank: item?.bank || 0,
            cash: item?.cash || 0,
            totalPayout: item.totalPayout || 0,
            totalHours: item.totalHrs || 0,
            gross: item?.gross || 0,
            category: item?.category || "",
            payrate: item?.payrate || 0,
            nonXeroPayrate: item?.nonXeroPayrate || 0,
            xeroPayrate: item?.xeroPayrate || 0,
            xeroHours: item?.xeroHours || 0,
            xeroPay: item?.xeroPay || 0,
            includeXeroHours: item?.includeXeroHours ?? false,
            kiwiSaver: item?.kiwiSaver || 0,
            fuelSupport: item?.fuelSupport || 0,
            actionBy: currentUser?.id || null,
          },
        }),
      );
    } else {
      dispatch(
        markPayoutUncomplete({
          staffId: item.id,
          periodStart,
          periodEnd,
          actionBy: currentUser?.id || null,
        }),
      );
    }
  };

  const handleDownloadPayoutList = () =>
    downloadPayoutList(selectedPayoutData, payPeriodText);

  const handleDownloadPayoutCard = () =>
    downloadPayoutCard(selectedPayoutData, payPeriodText, {
      afterHourRate,
      holidayRate,
    });

  const handleRecalculate = () => {
    setRecalculating(true);
    dispatch(
      calculatePayoutDailyData((response) => {
        setRecalculating(false);
        if (response?.status === 200) {
          message.success("Recalculate successfully");
          return;
        }
        message.error(response?.data || "Recalculate failed");
      }),
    );
  };

  // -------------------------- RENDER --------------------------
  const renderBody = () => {
    return (
      <Row className="payout-list-body">
        <Col xl={24} lg={24} md={24} xs={24}>
          <div className="payout-page">
            <PayoutToolbar
              currentWeekStart={currentWeekStart}
              keyword={keyword}
              setKeyword={setKeyword}
              setPage={setPage}
              selectedRowKeys={selectedRowKeys}
              handleWeekChange={handleWeekChange}
              handlePreviousWeek={handlePreviousWeek}
              handleNextWeek={handleNextWeek}
              nextDisabled={nextDisabled}
              handleCompletePayout={handleCompletePayout}
              downloadPopup={() => setOpenDownloadModal(true)}
              totalCash={totalCash}
              totalBank={totalBank}
              totalPayout={totalPayout}
              formatMoney={formatMoney}
              onRecalculate={handleRecalculate}
              recalculating={recalculating}
            />
            <PayoutTable
              filteredData={filteredData}
              loading={loading}
              page={page}
              pageSize={pageSize}
              onPageChange={(p, ps) => {
                setPage(p);
                setPageSize(ps);
              }}
              selectedRowKeys={selectedRowKeys}
              handleSelectAllPage={handleSelectAllPage}
              handleSelectOne={handleSelectOne}
              handleToggleStatus={handleToggleStatus}
              setViewingStaff={setViewingStaff}
              formatMoney={formatMoney}
              formatHours={formatHours}
            />
          </div>
        </Col>
      </Row>
    );
  };

  // -------------------------- MAIN --------------------------
  return (
    <Layout className="site-layout full">
      <Content className="content payout-list-content">
        <div style={{ paddingLeft: 0 }}>
          <LayoutBreadCrumb data={breadCrumb} />
        </div>
        {renderBody()}
      </Content>
      <PayoutCardDialog
        viewingStaff={viewingStaff}
        setViewingStaff={setViewingStaff}
        formatMoney={formatMoney}
        formatHours={formatHours}
        payPeriod={payPeriodText}
        afterHourRate={afterHourRate}
        holidayRate={holidayRate}
      />
      <PayoutDownloadOptionDialog
        open={openDownloadModal}
        onClose={() => setOpenDownloadModal(false)}
        onDownloadPayoutList={() => {
          setOpenDownloadModal(false);
          handleDownloadPayoutList();
        }}
        onDownloadPayoutCard={() => {
          setOpenDownloadModal(false);
          handleDownloadPayoutCard();
        }}
      />
      <LayoutFooter />
    </Layout>
  );
};

export default PayoutScreen;
